import type { Container, Ticker } from 'pixi.js';

import { OBS_SAFE_AREA, OVERLAY_HEIGHT, OVERLAY_WIDTH, WORLD_TILE_HEIGHT, WORLD_TILE_WIDTH } from '../../game/config';
import type { GameEvent } from '../events/gameEvents';
import type { ActiveEvent, GameWorldSnapshot } from '../world/types';
import type { CameraTarget } from './cameraTypes';

export function zoomForKingdomGrowth(level: number, buildingCount = 0, population = 0): number {
  if (buildingCount >= 24 || population >= 30) return 0.76;
  if (buildingCount >= 19 || population >= 22) return 0.8;
  if (buildingCount >= 15 || population >= 15) return 0.84;
  if (level <= 1) return 1;
  if (level === 2) return 0.94;
  if (level === 3) return 0.88;
  if (level === 4) return 0.84;
  if (level === 5) return 0.8;
  return 0.76;
}

export class CameraDirector {
  private target: CameraTarget = { x: 0, y: 0, zoom: 1 };
  private overviewTarget: CameraTarget = { x: 0, y: 0, zoom: 1 };
  private idleTime = 0;
  private mapWidth = 1;
  private mapHeight = 1;
  private temporaryFocusMs = 0;
  private overviewSignature = '';
	private manualZoomStep = 0;
	private lastSnapshot?: GameWorldSnapshot;

  constructor(
    private readonly worldRoot: Container,
    private readonly options: { visualPolish?: boolean; manualZoomStep?: number } = {},
  ) {
	this.manualZoomStep = this.normalizeManualZoomStep(options.manualZoomStep ?? 0);
  }

  focusSnapshot(snapshot: GameWorldSnapshot): void {
	this.lastSnapshot = snapshot;
    this.mapWidth = snapshot.map.width * WORLD_TILE_WIDTH;
    this.mapHeight = snapshot.map.height * WORLD_TILE_HEIGHT;
    this.overviewTarget = this.createOverviewTarget(snapshot);
    this.target = this.overviewTarget;
    this.overviewSignature = this.snapshotSignature(snapshot);
    this.applyStaticTarget();
  }

  syncSnapshot(snapshot: GameWorldSnapshot): void {
	this.lastSnapshot = snapshot;
    this.mapWidth = snapshot.map.width * WORLD_TILE_WIDTH;
    this.mapHeight = snapshot.map.height * WORLD_TILE_HEIGHT;
    const signature = this.snapshotSignature(snapshot);
    if (signature === this.overviewSignature) return;
    this.overviewSignature = signature;
    this.overviewTarget = this.createOverviewTarget(snapshot);
    if (this.temporaryFocusMs <= 0) this.target = this.overviewTarget;
  }

	setManualZoomStep(step: number): void {
		const normalized = this.normalizeManualZoomStep(step);
		if (normalized === this.manualZoomStep) return;
		this.manualZoomStep = normalized;
		if (!this.lastSnapshot) return;
		this.overviewTarget = this.createOverviewTarget(this.lastSnapshot);
		this.target = this.overviewTarget;
		this.temporaryFocusMs = 0;
	}

  handleEvent(event: GameEvent): void {
    if (event.type === 'building_completed') {
      this.setTemporaryTarget({
        x: this.worldToScreenX(event.x, event.y) - OVERLAY_WIDTH / 2,
        y: this.worldToScreenY(event.x, event.y) - OVERLAY_HEIGHT / 2,
        zoom: 1,
      });
      return;
    }

    if (event.type === 'attack_started' || event.type === 'raid_warning') {
      this.setTemporaryTarget({ x: 520, y: 260, zoom: 1.02 });
      return;
    }

    if (event.type === 'village_level_up') {
      this.setTemporaryTarget({ x: 120, y: 80, zoom: 0.82 });
    }
  }

  handleWorldEvent(event: ActiveEvent): void {
    if (event.type === 'join' || event.type === 'level_up') {
      this.setTemporaryTarget({ x: 420, y: 205, zoom: this.options.visualPolish ? 1.1 : 1.04 });
      return;
    }

    if (event.type === 'project' || event.type === 'vote' || event.type === 'quest') {
      this.setTemporaryTarget({ x: 570, y: 260, zoom: this.options.visualPolish ? 1.08 : 1 });
    }
  }

  update(ticker: Ticker): void {
    const deltaSeconds = ticker.deltaMS / 1000;
    if (this.temporaryFocusMs > 0) {
      this.temporaryFocusMs = Math.max(0, this.temporaryFocusMs - ticker.deltaMS);
      if (this.temporaryFocusMs === 0) this.target = this.overviewTarget;
    }
    this.idleTime += deltaSeconds;
    const idleDriftX = this.options.visualPolish ? 0 : Math.sin(this.idleTime * 0.14) * 30;
    const idleDriftY = this.options.visualPolish ? 0 : Math.cos(this.idleTime * 0.1) * 18;
    const drifted = this.clampTarget({
      ...this.target,
      x: this.target.x + idleDriftX,
      y: this.target.y + idleDriftY,
    });
    const desiredX = -drifted.x * drifted.zoom + OBS_SAFE_AREA.left;
    const desiredY = -drifted.y * drifted.zoom + OBS_SAFE_AREA.top;
    const desiredScale = this.target.zoom;
    const ease = Math.min(1, deltaSeconds * (this.options.visualPolish ? 1.45 : 1.8));

    this.worldRoot.x += (desiredX - this.worldRoot.x) * ease;
    this.worldRoot.y += (desiredY - this.worldRoot.y) * ease;
    this.worldRoot.scale.x += (desiredScale - this.worldRoot.scale.x) * ease;
    this.worldRoot.scale.y += (desiredScale - this.worldRoot.scale.y) * ease;
  }

  getState(): { readonly zoom: number; readonly targetZoom: number; readonly growthZoom: number } {
    return {
      zoom: this.worldRoot.scale.x,
      targetZoom: this.target.zoom,
      growthZoom: this.overviewTarget.zoom,
    };
  }

  private applyStaticTarget(): void {
    this.worldRoot.position.set(
      Math.round(-this.target.x * this.target.zoom + OBS_SAFE_AREA.left),
      Math.round(-this.target.y * this.target.zoom + OBS_SAFE_AREA.top),
    );
    this.worldRoot.scale.set(this.target.zoom);
  }

  private worldToScreenX(x: number, y: number): number {
    void y;
    return x * WORLD_TILE_WIDTH + WORLD_TILE_WIDTH / 2;
  }

  private worldToScreenY(x: number, y: number): number {
    void x;
    return y * WORLD_TILE_HEIGHT + WORLD_TILE_HEIGHT / 2;
  }

  private createOverviewTarget(snapshot: GameWorldSnapshot): CameraTarget {
    const growthZoom = zoomForKingdomGrowth(
      snapshot.progressionLevel,
      snapshot.buildings.length,
      snapshot.gameplay.population,
    );
    const bounds = this.settlementBounds(snapshot);
    const availableWidth = OVERLAY_WIDTH - OBS_SAFE_AREA.left - OBS_SAFE_AREA.right;
    const availableHeight = OVERLAY_HEIGHT - OBS_SAFE_AREA.top - OBS_SAFE_AREA.bottom;
    const fitZoom = Math.min(
      1,
      availableWidth / Math.max(1, bounds.right - bounds.left),
      availableHeight / Math.max(1, bounds.bottom - bounds.top),
    );
    const automaticZoom = Math.min(growthZoom, fitZoom);
	const fillZoom = Math.max(availableWidth / this.mapWidth, availableHeight / this.mapHeight);
	const requestedZoom = automaticZoom * (1 + this.manualZoomStep * 0.08);
	const zoom = Math.max(fillZoom, Math.min(1.16, requestedZoom));
    const viewportWidth = (OVERLAY_WIDTH - OBS_SAFE_AREA.left - OBS_SAFE_AREA.right) / zoom;
    const viewportHeight = (OVERLAY_HEIGHT - OBS_SAFE_AREA.top - OBS_SAFE_AREA.bottom) / zoom;
    return this.clampTarget({
      x: (bounds.left + bounds.right) / 2 - viewportWidth / 2,
      y: (bounds.top + bounds.bottom) / 2 - viewportHeight / 2,
      zoom,
    });
  }

  private snapshotSignature(snapshot: GameWorldSnapshot): string {
    return [
      snapshot.progressionLevel,
      snapshot.buildings.length,
      snapshot.gameplay.population,
      snapshot.map.width,
      snapshot.map.height,
      ...snapshot.buildings
        .map((building) => `${building.id}@${building.x.toFixed(2)},${building.y.toFixed(2)}`)
        .sort(),
    ].join(':');
  }

  private setTemporaryTarget(target: CameraTarget, durationMs = 5_000): void {
    if (this.options.visualPolish) {
      this.target = this.overviewTarget;
      this.temporaryFocusMs = 0;
      return;
    }
    this.target = this.clampTarget(target);
    this.temporaryFocusMs = durationMs;
  }

  private settlementBounds(snapshot: GameWorldSnapshot): {
    readonly left: number;
    readonly top: number;
    readonly right: number;
    readonly bottom: number;
  } {
    if (!snapshot.buildings.length) {
      return { left: 0, top: 0, right: this.mapWidth, bottom: this.mapHeight };
    }
    const extents = snapshot.buildings.map((building) => {
      const footprintWidth = building.building?.footprint.width ?? (building.kind.startsWith('wall_') ? 1 : 2);
      const footprintHeight = building.building?.footprint.height ?? (building.kind.startsWith('wall_') ? 1 : 2);
      const centerX = building.x * WORLD_TILE_WIDTH + WORLD_TILE_WIDTH / 2;
      const footY = building.y * WORLD_TILE_HEIGHT + WORLD_TILE_HEIGHT;
      const halfWidth = Math.max(54, footprintWidth * WORLD_TILE_WIDTH * 0.72);
      const height = Math.max(92, footprintHeight * WORLD_TILE_HEIGHT * 1.15);
      return {
        left: centerX - halfWidth,
        top: footY - height,
        right: centerX + halfWidth,
        bottom: footY + 36,
      };
    });
    return {
      left: Math.min(...extents.map((extent) => extent.left)) - 96,
      top: Math.min(...extents.map((extent) => extent.top)) - 120,
      right: Math.max(...extents.map((extent) => extent.right)) + 96,
      bottom: Math.max(...extents.map((extent) => extent.bottom)) + 80,
    };
  }

  private clampTarget(target: CameraTarget): CameraTarget {
    const viewportWidth = (OVERLAY_WIDTH - OBS_SAFE_AREA.left - OBS_SAFE_AREA.right) / target.zoom;
    const viewportHeight = (OVERLAY_HEIGHT - OBS_SAFE_AREA.top - OBS_SAFE_AREA.bottom) / target.zoom;
    return {
      ...target,
      x: this.clampAxis(target.x, this.mapWidth, viewportWidth),
      y: this.clampAxis(target.y, this.mapHeight, viewportHeight),
    };
  }

  private clampAxis(value: number, mapSize: number, viewportSize: number): number {
    if (viewportSize >= mapSize) return (mapSize - viewportSize) / 2;
    return Math.max(0, Math.min(value, mapSize - viewportSize));
  }

	private normalizeManualZoomStep(step: number): number {
		return Math.max(-3, Math.min(3, Number.isFinite(step) ? Math.round(step) : 0));
	}
}
